import { createLocalizationMetrics, createLocalizationRuntime, createPreferencesController, createPreviewController, createServerErrorResolver, useLocalization, usePreferencesAccount, type MessageParams } from "@voltie/localization-web";
import { embedded } from "@voltie/localization-web/embedded";
import { serverErrorRegistry } from "@voltie/localization-web/server-errors";
import { useAuth } from "./AuthContext";

/** In-page counters of missing keys, fallbacks and catalog failures of this runtime (diagnostics; nothing is sent). */
export const localizationMetrics = createLocalizationMetrics();

/** The one localization runtime of the dashboard: embedded English first, then /l10n/dashboard/v<contract>/manifest.json. */
export const localization = createLocalizationRuntime({ embedded, onEvent: localizationMetrics.onEvent });

/** Catalog key and params of a server error (LOC-707); fallbacks are counted in localizationMetrics. */
export const serverErrors = createServerErrorResolver({ registry: serverErrorRegistry, onEvent: localizationMetrics.onEvent });

/** The signed-in account's stored UI language, applied to the runtime; account controls use usePreferences(languagePreferences). */
export const languagePreferences = createPreferencesController({ runtime: localization });

/** Tester-only translation preview of this tab (memory only, never the profile); follows languagePreferences' account. */
export const languagePreview = createPreviewController({ runtime: localization, preferences: languagePreferences });

/** Stable translate function; outside LocalizationProvider (component tests) it renders the embedded English. */
export function useT() {
  return useLocalization(localization).t;
}

/** The translate function as passed to pure label helpers (always the active catalog when called). */
export type Translate = (key: string, params?: MessageParams) => string;

/** `t` plus the identity of the active catalog. `t` is stable, so a useMemo that builds
 *  labels lists `catalog` as a dependency to follow a language or catalog switch. */
export function useCatalog(): { t: Translate; catalog: string } {
  const { t, language, revision } = useLocalization(localization);
  return { t, catalog: `${language}@${revision}` };
}

/** Follows the server's identity projection (/api/auth/me): profile language when signed in, browser rule otherwise.
 *  When the session turns out to be another account's, /api/auth/me is asked again. */
export function LanguagePreferenceSync() {
  const { user, isLoading, refetch } = useAuth();
  usePreferencesAccount(languagePreferences, isLoading ? undefined : (user?.id ?? null), refetch);
  return null;
}
